import * as RadixTabs from "@radix-ui/react-tabs";
import React, { useEffect, useState } from "react";
import { Button } from "../../components/ui/button";
import { useTranslation } from "react-i18next";
import { cn } from "../../lib/utils";
import MagnifyingGlass from "../../assets/icons/MagnifyingGlass";

export enum InputType {
  SINGLE,
  PICKFROMEXISTING,
  ALLOWALL,
}

const TAB_CLS = cn(
  "px-4 py-2 text-[12px] font-medium border-b-2 border-transparent -mb-px transition-colors bg-transparent",
  "data-[state=active]:text-[var(--sc-text)] data-[state=active]:border-[var(--sc-text)]",
  "data-[state=inactive]:text-[var(--sc-muted)] hover:text-[var(--sc-text)]",
  "disabled:opacity-40 disabled:cursor-not-allowed focus-visible:outline-none"
);

const Modal = ({
  headerTitle,
  setShowModal,
  saveItem,
  saveExisting,
  existingItems,
  inputType,
}: {
  headerTitle: string;
  setShowModal: (v: boolean) => void;
  saveItem: (name: string) => void;
  saveExisting: (questionId: string) => void;
  existingItems: { name: string; id: string }[];
  inputType: InputType;
}) => {
  const { t } = useTranslation();
  const [value, setValue] = useState("");
  const [filteredItems, setFilteredItems] = useState(existingItems);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    setFilteredItems(existingItems);
  }, [existingItems]);

  const onSearchChange = ({ target }: React.ChangeEvent<HTMLInputElement>) =>
    setFilteredItems(
      existingItems.filter(({ name }) =>
        name.toLocaleLowerCase().includes(target.value.toLocaleLowerCase())
      )
    );

  const toggleItem = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/25">
        <div className="bg-[var(--sc-surface)] rounded-[14px] w-[560px] shadow-[0_20px_60px_rgba(0,0,0,0.2)] overflow-hidden">
          {/* Header */}
          <div className="px-5 pt-[18px] flex items-center justify-between">
            <h3 className="text-[15px] font-semibold text-[var(--sc-text)]">{headerTitle}</h3>
            <button
              className="w-7 h-7 rounded-[6px] border border-[var(--sc-border)] bg-[var(--sc-surface)] flex items-center justify-center text-[var(--sc-muted)] text-sm hover:bg-[var(--sc-bg)] transition-colors"
              onClick={() => setShowModal(false)}
            >
              ✕
            </button>
          </div>

          {/* Tabs */}
          <RadixTabs.Root defaultValue={inputType === InputType.PICKFROMEXISTING ? "existing" : "new"}>
            <div className="px-5 mt-[14px] border-b border-[var(--sc-border)]">
              <RadixTabs.List className="flex gap-0 bg-transparent">
                <RadixTabs.Trigger
                  value="new"
                  disabled={inputType === InputType.PICKFROMEXISTING}
                  className={TAB_CLS}
                >
                  {t("new")}
                </RadixTabs.Trigger>
                <RadixTabs.Trigger
                  value="existing"
                  disabled={inputType === InputType.SINGLE}
                  className={TAB_CLS}
                >
                  {t("chooseFromExisting")}
                </RadixTabs.Trigger>
              </RadixTabs.List>
            </div>

            {/* New question tab */}
            <RadixTabs.Content value="new">
              <div className="px-5 py-4">
                <textarea
                  className="w-full bg-[#F2F2F0] border border-transparent rounded-[6px] px-[10px] py-2 text-[12px] text-[var(--sc-text)] min-h-[100px] outline-none resize-none leading-relaxed transition-colors focus:bg-[#EAF0F8] focus:border focus:border-[#99AECB]"
                  placeholder="Wpisz treść pytania..."
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                />
              </div>
              <div className="px-5 py-[14px] border-t border-[var(--sc-border)] flex justify-end gap-2">
                <Button variant="secondary" onClick={() => setShowModal(false)}>
                  {t("close")}
                </Button>
                <Button
                  onClick={() => {
                    saveItem(value);
                    setShowModal(false);
                  }}
                >
                  {t("save")}
                </Button>
              </div>
            </RadixTabs.Content>

            {/* Pick from existing tab */}
            <RadixTabs.Content value="existing">
              <div className="px-5 py-4">
                <div className="flex items-center gap-2 border border-[var(--sc-border)] rounded-[8px] px-3 py-2 bg-[var(--sc-bg)] mb-3">
                  <span className="text-[var(--sc-muted)] flex-shrink-0">
                    <MagnifyingGlass />
                  </span>
                  <input
                    type="search"
                    placeholder={"Szukaj pytań w bibliotece..."}
                    onChange={onSearchChange}
                    className="flex-1 bg-transparent border-none outline-none text-[12px] text-[var(--sc-text)] placeholder:text-[var(--sc-muted)]"
                  />
                </div>
                <div className="max-h-[220px] overflow-y-auto">
                  {filteredItems.map((item) => {
                    const selected = selectedIds.has(item.id);
                    return (
                      <div
                        key={item.id}
                        onClick={() => toggleItem(item.id)}
                        className={cn(
                          "flex items-start gap-[10px] px-3 py-[10px] rounded-[6px] cursor-pointer text-[12px] text-[var(--sc-text)] leading-[1.4] transition-colors select-none",
                          selected ? "bg-[#FEF3E2]" : "hover:bg-[var(--sc-bg)]"
                        )}
                      >
                        <div
                          className={cn(
                            "w-4 h-4 rounded-[4px] border-[1.5px] border-[var(--sc-border)] flex-shrink-0 mt-[1px] flex items-center justify-center text-[9px]",
                            selected && "bg-[#F4A832] border-[#F4A832] text-white"
                          )}
                        >
                          {selected && "✓"}
                        </div>
                        {item.name}
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="px-5 py-[14px] border-t border-[var(--sc-border)] flex justify-end gap-2">
                <Button variant="secondary" onClick={() => setShowModal(false)}>
                  {t("close")}
                </Button>
                <Button
                  disabled={selectedIds.size === 0}
                  onClick={() => {
                    selectedIds.forEach((id) => saveExisting(id));
                    setShowModal(false);
                  }}
                >
                  Dodaj zaznaczone ({selectedIds.size})
                </Button>
              </div>
            </RadixTabs.Content>
          </RadixTabs.Root>
        </div>
      </div>
    </>
  );
};

export default function AddItemWithExistingPicker({
  saveNew,
  text,
  children,
  saveExisting,
  items,
  inputType = InputType.ALLOWALL,
  buttonStyle = "",
}: {
  text: string;
  saveNew: (name: string) => void;
  saveExisting: (questionId: string) => void;
  children?: React.ReactNode;
  items?: () => Promise<{ name: string; id: string }[]>;
  inputType?: InputType;
  buttonStyle?: string;
}) {
  const [showModal, setShowModal] = React.useState(false);
  const [existingItems, setExistingItems] = useState<{ name: string; id: string }[]>([]);

  useEffect(() => {
    if (showModal && items) {
      items().then((result) => setExistingItems(result || []));
    }
  }, [showModal, items]);

  return (
    <>
      <Button
        size="sm"
        className={buttonStyle}
        variant="secondary"
        onClick={() => setShowModal(true)}
      >
        {children}
      </Button>
      {showModal && (
        <Modal
          headerTitle={text}
          saveItem={saveNew}
          setShowModal={setShowModal}
          saveExisting={saveExisting}
          existingItems={existingItems}
          inputType={inputType}
        />
      )}
    </>
  );
}


